/**
 * @purpose: mengelola halaman operasional bagging untuk header, scan AWB bulk, seleksi koli parsial, monitoring hasil, dan preview cetak.
 * @called_by: views/app_manifest/manifest_bagging_pg.html.
 * @deps: urlServices, toaster, modalService, modalFactory.
 * @public: controller AngularJS baggingCtrl.
 * @side_effects: memanggil API bagging, menyimpan draft AWB parsial ke sessionStorage, dan mengubah state form/detail bagging aktif.
 */
app.controller('baggingCtrl', ['$scope', 'urlServices', 'toaster', 'modalService', 'modalFactory', '_API_URL', '$window', function ($scope, urlServices, toaster, modalService, modalFactory, _API_URL, $window) {
    var apiBase = 'api/bagging-entry';

    $scope.loading = false;
    $scope.masterLoading = false;
    $scope.savingHeader = false;
    $scope.savingItem = false;
    $scope.searchBagId = '';
    $scope.packageTypes = ['KARUNG', 'PETI', 'PETI KAYU', 'PALLET', 'BOX', 'LAINNYA'];
    $scope.transportModes = ['AIR', 'LAND', 'SEA'];
    $scope.referenceMasters = {
        hubs: [],
        destinations: []
    };
    $scope.lastGeneratedKey = '';
    $scope.suspendAutoNumberSync = false;
    $scope.datePickerOptions = angular.extend({}, $scope.$root.globalFormatDateRangePickerSingle || {}, {
        autoApply: true
    });
    $scope.awbDetailLoading = false;
    $scope.savingSelections = false;
    $scope.awbDrafts = [];
    $scope.activeAwbDraft = null;
    $scope.suspendMeasuredWeightSync = false;
    $scope.headerMeasuredWeightAuto = true;
    $scope.cameraScanner = {
        supported: !!($window.navigator && $window.navigator.mediaDevices && $window.navigator.mediaDevices.getUserMedia && $window.BarcodeDetector),
        active: false,
        loading: false,
        error: '',
        lastScan: '',
        lastMessage: '',
        totalScanned: 0
    };
    $scope.offlineState = {
        isOnline: !($window.navigator && $window.navigator.onLine === false),
        queueCount: 0,
        syncing: false,
        draftPersisted: false,
        lastQueuedAt: '',
        lastSyncedAt: ''
    };
    $scope.saveFeedback = {
        type: '',
        message: ''
    };

    var activeCameraStream = null;
    var activeCameraDetector = null;
    var cameraScanFrame = null;
    var cameraLastSeenMap = {};
    var onlineEventHandler = null;
    var offlineEventHandler = null;

    function createHeaderForm() {
        return {
            BagId: '',
            OriginalBagId: '',
            ManifestDate: moment().format('YYYY-MM-DD'),
            HostCode: '',
            HostName: '',
            DestinationHostCode: '',
            DestinationHostName: '',
            AgentCode: '',
            AgentContactType: '',
            AgentName: '',
            ModTrans: 'LAND',
            PackageType: 'KARUNG',
            BagNumber: '',
            ActualCollie: '',
            ActualWeight: '',
            MeasuredWeight: '',
            VolumeActual: '',
            LengthCm: '',
            WidthCm: '',
            HeightCm: '',
            LabelHostName: '',
            LabelDestinationName: '',
            Description: ''
        };
    }

    function createItemForm() {
        return {
            AWB: '',
            BulkAwbs: '',
            CollieTotal: '',
            CollieBagging: '',
            WeightTotal: '',
            WeightBagging: '',
            ActualWeight: '',
            LengthCm: '',
            WidthCm: '',
            HeightCm: '',
            PackageType: ($scope.headerForm && $scope.headerForm.PackageType) || 'KARUNG',
            Note: '',
            UOM: 'KG'
        };
    }

    $scope.headerForm = createHeaderForm();
    $scope.itemForm = createItemForm();
    $scope.baggingDetail = {
        header: null,
        items: [],
        summaryByDestination: [],
        summaryByCity: []
    };

    function normalizeTextValue(value) {
        return String(value === undefined || value === null ? '' : value).trim();
    }

    function normalizeUpperValue(value) {
        return normalizeTextValue(value).toUpperCase();
    }

    function normalizeComparableValue(value) {
        return normalizeUpperValue(value).replace(/\s+/g, ' ');
    }

    function hasOwnPropertyValue(source, propertyName) {
        return !!source && Object.prototype.hasOwnProperty.call(source, propertyName);
    }

    function normalizeIntegerValue(value, fallbackValue) {
        var normalized = parseInt(normalizeTextValue(value), 10);

        if (isNaN(normalized)) {
            return fallbackValue;
        }

        return normalized;
    }

    function normalizeDecimalValue(value, fallbackValue) {
        var normalized = parseFloat(normalizeTextValue(value));

        if (isNaN(normalized)) {
            return fallbackValue;
        }

        return normalized;
    }

    function normalizeNumberInputValue(value) {
        if (normalizeTextValue(value) === '') {
            return '';
        }

        return normalizeDecimalValue(value, '');
    }

    function normalizeBooleanValue(value) {
        if (value === true || value === false) {
            return value;
        }

        return ['1', 'TRUE', 'T', 'YES', 'Y'].indexOf(normalizeUpperValue(value)) >= 0;
    }

    function getProportionalWeight(totalWeight, totalCollie, selectedCollie) {
        var normalizedTotalWeight = normalizeDecimalValue(totalWeight, 0);
        var normalizedTotalCollie = normalizeIntegerValue(totalCollie, 0);
        var normalizedSelectedCollie = normalizeIntegerValue(selectedCollie, 0);

        if (normalizedSelectedCollie <= 0 || normalizedTotalWeight <= 0) {
            return 0;
        }
        if (normalizedTotalCollie <= 0) {
            return parseFloat(normalizedTotalWeight.toFixed(2));
        }

        return parseFloat(((normalizedTotalWeight / normalizedTotalCollie) * normalizedSelectedCollie).toFixed(2));
    }

    function getVolumetricDivider(modTrans) {
        // LAND / SEA = /4000, AIR = /6000
        return normalizeUpperValue(modTrans) === 'AIR' ? 6000 : 4000;
    }

    function calculateMeasuredWeight(lengthCm, widthCm, heightCm, modTrans) {
        var normalizedLength = normalizeDecimalValue(lengthCm, 0);
        var normalizedWidth = normalizeDecimalValue(widthCm, 0);
        var normalizedHeight = normalizeDecimalValue(heightCm, 0);
        var divider = getVolumetricDivider(modTrans);

        if (!(normalizedLength > 0) || !(normalizedWidth > 0) || !(normalizedHeight > 0)) {
            return '';
        }

        // RoundUP to integer, no decimal places
        return Math.ceil((normalizedLength * normalizedWidth * normalizedHeight) / divider);
    }

    function syncHeaderVolumeActual() {
        if ($scope.suspendMeasuredWeightSync || !$scope.headerMeasuredWeightAuto) {
            return;
        }
        var volumetric = calculateMeasuredWeight($scope.headerForm.LengthCm, $scope.headerForm.WidthCm, $scope.headerForm.HeightCm, $scope.headerForm.ModTrans);
        $scope.headerForm.VolumeActual = volumetric === '' ? '' : volumetric;
    }

    function triggerHapticFeedback(type) {
        var pattern;

        if (!$window.navigator || !angular.isFunction($window.navigator.vibrate)) {
            return;
        }

        if (type === 'error') {
            pattern = [180, 70, 180];
        } else if (type === 'warning') {
            pattern = [120];
        } else {
            pattern = [70];
        }

        $window.navigator.vibrate(pattern);
    }

    function isClientOnline() {
        return !($window.navigator && $window.navigator.onLine === false);
    }

    function getRequestedBagIdFromUrl() {
        var search = ($window.location && $window.location.search) ? $window.location.search : '';
        var match;

        if (!search) {
            return '';
        }

        match = search.match(/[?&]bagId=([^&]+)/i);
        return match ? normalizeUpperValue(decodeURIComponent(match[1].replace(/\+/g, ' '))) : '';
    }


    function normalizeDatePickerValue(value) {
        var parsed;

        if (moment.isMoment(value)) {
            return value.clone();
        }

        parsed = moment(normalizeTextValue(value), ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
        if (!parsed.isValid()) {
            parsed = moment(value);
        }

        return parsed.isValid() ? parsed : moment();
    }

    function syncManifestDateValue(value) {
        var parsed = normalizeDatePickerValue(value);

        $scope.manifestDateModel = parsed;
        $scope.headerForm.ManifestDate = parsed.format('YYYY-MM-DD');
    }

    function rowOrEmpty(value) {
        return normalizeTextValue(value);
    }

    function setSaveFeedback(type, message) {
        $scope.saveFeedback = {
            type: type || '',
            message: normalizeTextValue(message)
        };
    }

    function resetLoadedBaggingState(options) {
        var actionOptions = angular.extend({
            preserveSearchValue: ''
        }, options || {});

        stopCameraScan(true);
        clearDraftState();
        $scope.headerForm = createHeaderForm();
        $scope.lastGeneratedKey = '';
        $scope.suspendAutoNumberSync = true;
        syncManifestDateValue($scope.headerForm.ManifestDate);
        $scope.baggingDetail = {
            header: null,
            items: [],
            summaryByDestination: [],
            summaryByCity: []
        };
        $scope.headerMeasuredWeightAuto = true;
        resetItemForm();
        syncHeaderReferenceState();
        $scope.searchBagId = actionOptions.preserveSearchValue || '';
        if ($window.history && $window.history.replaceState) {
            $window.history.replaceState({}, $window.document ? $window.document.title : '', '/bagging');
        }
        $scope.$evalAsync(function () {
            $scope.suspendAutoNumberSync = false;
        });
    }

    function parseBulkAwbList(value) {
        var seen = {};

        return normalizeTextValue(value).split(/[,;\n\r\t ]+/).map(function (item) {
            return normalizeUpperValue(item);
        }).filter(function (item) {
            if (!item || seen[item]) {
                return false;
            }

            seen[item] = true;
            return true;
        });
    }

    function normalizeScannedAwb(value) {
        return normalizeUpperValue(value).replace(/[^A-Z0-9-]/g, '');
    }

    function appendAwbToBulkInput(rawAwb) {
        var normalizedAwb = normalizeScannedAwb(rawAwb);
        var awbList;

        if (!normalizedAwb) {
            return 'invalid';
        }

        awbList = parseBulkAwbList(($scope.itemForm && $scope.itemForm.BulkAwbs) || '');
        if (awbList.indexOf(normalizedAwb) >= 0) {
            return 'duplicate';
        }

        awbList.push(normalizedAwb);
        $scope.itemForm.BulkAwbs = awbList.join('\n');
        saveDraftSession();
        return 'added';
    }

    function getCameraVideoElement() {
        return $window.document ? $window.document.getElementById('baggingAwbCameraVideo') : null;
    }

    function getBarcodeDetectorInstance() {
        if (!$window.BarcodeDetector) {
            return null;
        }
        if (activeCameraDetector) {
            return activeCameraDetector;
        }

        try {
            activeCameraDetector = new $window.BarcodeDetector({
                formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'itf']
            });
        } catch (detectorError) {
            activeCameraDetector = new $window.BarcodeDetector();
        }

        return activeCameraDetector;
    }

    function stopCameraScan(resetMessage) {
        if (cameraScanFrame) {
            $window.cancelAnimationFrame(cameraScanFrame);
            cameraScanFrame = null;
        }

        if (activeCameraStream) {
            angular.forEach(activeCameraStream.getTracks(), function (track) {
                if (track && angular.isFunction(track.stop)) {
                    track.stop();
                }
            });
            activeCameraStream = null;
        }

        $scope.cameraScanner.active = false;
        $scope.cameraScanner.loading = false;
        if (resetMessage) {
            $scope.cameraScanner.lastMessage = '';
        }
    }

    function scheduleCameraFrameScan() {
        if (!$scope.cameraScanner.active) {
            return;
        }

        cameraScanFrame = $window.requestAnimationFrame(scanCameraFrame);
    }

    function handleDetectedCameraValue(rawValue) {
        var normalizedAwb = normalizeScannedAwb(rawValue);
        var seenAt = cameraLastSeenMap[normalizedAwb];
        var appendStatus;
        var now = Date.now();

        if (normalizedAwb && seenAt && now - seenAt < 1500) {
            return;
        }
        if (normalizedAwb) {
            cameraLastSeenMap[normalizedAwb] = now;
        }

        appendStatus = appendAwbToBulkInput(normalizedAwb);
        $scope.$evalAsync(function () {
            if (appendStatus === 'added') {
                $scope.cameraScanner.totalScanned += 1;
                $scope.cameraScanner.lastScan = normalizedAwb;
                $scope.cameraScanner.lastMessage = 'AWB ' + normalizedAwb + ' ditambahkan ke daftar scan';
                triggerHapticFeedback('success');
                return;
            }

            if (appendStatus === 'duplicate') {
                $scope.cameraScanner.lastMessage = 'AWB ' + normalizedAwb + ' sudah ada di daftar scan';
                return;
            }

            $scope.cameraScanner.lastMessage = 'Barcode tidak dikenali sebagai AWB';
            triggerHapticFeedback('error');
        });
    }

    function scanCameraFrame() {
        var barcodeDetector = getBarcodeDetectorInstance();
        var videoElement = getCameraVideoElement();

        if (!$scope.cameraScanner.active || !barcodeDetector || !videoElement) {
            return;
        }

        return barcodeDetector.detect(videoElement).then(function (results) {
            if (results && results.length && results[0].rawValue) {
                handleDetectedCameraValue(results[0].rawValue);
            }
            scheduleCameraFrameScan();
        }).catch(function () {
            scheduleCameraFrameScan();
        });
    }

    function normalizeError(error) {
        if (!error) {
            return 'Error tidak terdeteksi';
        }
        if (error.err) {
            return error.err;
        }
        if (error.message) {
            return error.message;
        }
        if (error.data && error.data.err) {
            return error.data.err;
        }
        return String(error);
    }

    function isOfflineLikeError(error) {
        var normalizedMessage = normalizeUpperValue(normalizeError(error));

        return !normalizedMessage || /NETWORK|OFFLINE|CONNECTION|INTERNET|ERR_NETWORK|FAILED TO FETCH|LOAD FAILED|STATUS 0|DISCONNECTED|TIMEOUT|NOT CONNECTED/.test(normalizedMessage);
    }

    function buildAgentDisplayValue(agentCode, agentName) {
        var normalizedCode = normalizeTextValue(agentCode);
        var normalizedName = normalizeTextValue(agentName);
        var escapedCode;
        var prefixPattern;

        if (normalizedCode && normalizedName) {
            escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            prefixPattern = new RegExp('^' + escapedCode + '\\s*-\\s*', 'i');
            while (prefixPattern.test(normalizedName)) {
                normalizedName = normalizeTextValue(normalizedName.replace(prefixPattern, ''));
            }
        }

        return normalizedName || normalizedCode;
    }

    function parseExtraPayload(value) {
        if (!value) {
            return {};
        }
        if (angular.isObject(value)) {
            return value;
        }

        try {
            return JSON.parse(value);
        } catch (error) {
            return {};
        }
    }

    function buildCurrentPrintPreviewUrl() {
        var bagId = normalizeTextValue($scope.headerForm.BagId);
        var manifestDate = normalizeDatePickerValue($scope.headerForm.ManifestDate || moment()).format('YYYY-MM-DD');

        if (!bagId) {
            return '';
        }

        return _API_URL + 'bagging-print-preview?reportType=detail&source=entry&returnUrl=' + encodeURIComponent('/bagging') + '&bagIds=' + encodeURIComponent(bagId) + '&startDate=' + encodeURIComponent(manifestDate) + '&endDate=' + encodeURIComponent(manifestDate);
    }

    function showConfirm(options, onConfirm) {
        modalService.showModal({}, angular.extend({
            closeButtonText: 'Batal',
            actionButtonText: 'Ya'
        }, options || {})).then(function (confirmed) {
            if (confirmed && onConfirm) {
                onConfirm();
            }
        }, function () {
            return true;
        });
    }

    function openLookup(functionName, params) {
        return modalFactory.open('lg', 'modal.list.html', 'modalListCtrl', {
            functionName: functionName,
            params: params || {}
        });
    }

    function findHub(code) {
        var normalized = normalizeUpperValue(code);
        var found = null;

        angular.forEach($scope.referenceMasters.hubs || [], function (row) {
            if (!found && normalizeUpperValue(row && row.hub_id) === normalized) {
                found = row;
            }
        });

        return found;
    }

    function syncHeaderReferenceState() {
        var host = findHub($scope.headerForm.HostCode);
        var destinationHost;
        var currentHostName = rowOrEmpty($scope.headerForm.HostName);
        var currentDestinationHostName = rowOrEmpty($scope.headerForm.DestinationHostName);
        var currentLabelHostName = rowOrEmpty($scope.headerForm.LabelHostName);
        var currentLabelDestinationName = rowOrEmpty($scope.headerForm.LabelDestinationName);

        $scope.headerForm.HostCode = normalizeUpperValue($scope.headerForm.HostCode);
        $scope.headerForm.DestinationHostCode = normalizeUpperValue($scope.headerForm.DestinationHostCode);

        $scope.headerForm.HostName = host ? rowOrEmpty(host.hub_name) : currentHostName;
        $scope.headerForm.LabelHostName = currentLabelHostName || $scope.headerForm.HostName;

        destinationHost = findHub($scope.headerForm.DestinationHostCode);
        $scope.headerForm.DestinationHostName = destinationHost ? rowOrEmpty(destinationHost.hub_name) : currentDestinationHostName;
        $scope.headerForm.LabelDestinationName = currentLabelDestinationName || $scope.headerForm.DestinationHostName;

        if (host && normalizeComparableValue(currentLabelHostName) === normalizeComparableValue(currentHostName)) {
            $scope.headerForm.LabelHostName = $scope.headerForm.HostName;
        }
        if (destinationHost && normalizeComparableValue(currentLabelDestinationName) === normalizeComparableValue(currentDestinationHostName)) {
            $scope.headerForm.LabelDestinationName = $scope.headerForm.DestinationHostName;
        }

        $scope.headerForm.BagNumber = $scope.headerForm.BagId || '';
        if (!$scope.itemForm.PackageType) {
            $scope.itemForm.PackageType = $scope.headerForm.PackageType || 'KARUNG';
        }
    }

    function getCurrentNumberKey() {
        return [
            normalizeUpperValue($scope.headerForm.HostCode),
            normalizeUpperValue($scope.headerForm.DestinationHostCode),
            normalizeTextValue($scope.headerForm.ManifestDate),
            normalizeTextValue($scope.headerForm.OriginalBagId)
        ].join('|');
    }

    function maybeGenerateBaggingNumber(forceMessage, callback) {
        var currentKey = getCurrentNumberKey();

        if (!$scope.headerForm.HostCode || !$scope.headerForm.DestinationHostCode || !$scope.headerForm.ManifestDate) {
            $scope.lastGeneratedKey = '';
            $scope.headerForm.BagId = '';
            $scope.headerForm.BagNumber = '';
            if (callback) {
                callback('HostCode, DestinationHostCode, dan tanggal bagging wajib diisi');
            }
            return;
        }
        if (!forceMessage && $scope.lastGeneratedKey === currentKey && $scope.headerForm.BagId) {
            if (callback) {
                callback(null, {
                    numeric: $scope.headerForm.BagId
                });
            }
            return;
        }

        return urlServices.simpanData(apiBase + '/number', {
            HostCode: $scope.headerForm.HostCode,
            DestinationHostCode: $scope.headerForm.DestinationHostCode,
            OriginalBagId: $scope.headerForm.OriginalBagId,
            ManifestDate: $scope.headerForm.ManifestDate
        }, function (err, result) {
            if (err) {
                if (forceMessage) {
                    toaster.pop('error', 'Bagging', normalizeError(err));
                }
                if (callback) {
                    return callback(err);
                }
                return;
            }

            $scope.headerForm.BagId = result && result.numeric ? result.numeric : '';
            $scope.headerForm.BagNumber = $scope.headerForm.BagId;
            $scope.lastGeneratedKey = currentKey;

            if (forceMessage) {
                toaster.pop('success', 'Bagging', 'Nomor bagging: ' + ($scope.headerForm.BagId || '-'));
            }
            if (callback) {
                return callback(null, result);
            }
        });
    }

    function mapHeaderToForm(header, options) {
        var actionOptions = angular.extend({
            restoreDraft: true
        }, options || {});
        var extraPayload;
        var agentPayload;
        var measuredWeightSuggestion;

        if (!header) {
            return;
        }

        extraPayload = parseExtraPayload(header.extra_payload);
        agentPayload = extraPayload && extraPayload.agent ? extraPayload.agent : {};

        $scope.suspendMeasuredWeightSync = true;
        $scope.headerForm.BagId = header.bag_id || '';
        $scope.headerForm.OriginalBagId = header.bag_id || '';
        $scope.suspendAutoNumberSync = true;
        syncManifestDateValue(header.manifest_date || moment());
        $scope.headerForm.HostCode = header.host_code || '';
        $scope.headerForm.HostName = header.host_name || '';
        $scope.headerForm.DestinationHostCode = header.destination_host_code || '';
        $scope.headerForm.DestinationHostName = header.destination_host_name || '';
        $scope.headerForm.AgentCode = agentPayload.code || '';
        $scope.headerForm.AgentContactType = agentPayload.contactType || '';
        $scope.headerForm.AgentName = buildAgentDisplayValue(agentPayload.code, header.agent_name || agentPayload.name);
        $scope.headerForm.ModTrans = header.mod_trans || 'LAND';
        $scope.headerForm.PackageType = header.package_type || 'KARUNG';
        $scope.headerForm.BagNumber = header.bag_number || header.bag_id || '';
        $scope.headerForm.ActualCollie = header.actual_collie || header.seal_number || '';
        $scope.headerForm.ActualWeight = hasOwnPropertyValue(header, 'actual_weight') ? normalizeNumberInputValue(header.actual_weight) : '';
        $scope.headerForm.MeasuredWeight = hasOwnPropertyValue(header, 'measured_weight') ? normalizeNumberInputValue(header.measured_weight) : '';
        $scope.headerForm.VolumeActual = hasOwnPropertyValue(header, 'volume_m3') ? normalizeNumberInputValue(header.volume_m3) : '';
        $scope.headerForm.LengthCm = hasOwnPropertyValue(header, 'length_cm') ? normalizeNumberInputValue(header.length_cm) : '';
        $scope.headerForm.WidthCm = hasOwnPropertyValue(header, 'width_cm') ? normalizeNumberInputValue(header.width_cm) : '';
        $scope.headerForm.HeightCm = hasOwnPropertyValue(header, 'height_cm') ? normalizeNumberInputValue(header.height_cm) : '';
        $scope.headerForm.LabelHostName = header.label_host_name || header.host_name || '';
        $scope.headerForm.LabelDestinationName = header.label_destination_name || header.destination_host_name || '';
        $scope.headerForm.Description = header.description || '';
        $scope.searchBagId = header.bag_id || '';
        $scope.lastGeneratedKey = getCurrentNumberKey();
        measuredWeightSuggestion = calculateMeasuredWeight($scope.headerForm.LengthCm, $scope.headerForm.WidthCm, $scope.headerForm.HeightCm, $scope.headerForm.ModTrans);
        $scope.headerMeasuredWeightAuto = $scope.headerForm.MeasuredWeight === '' || (measuredWeightSuggestion !== '' && normalizeDecimalValue($scope.headerForm.MeasuredWeight, -1) === normalizeDecimalValue(measuredWeightSuggestion, -2));
        syncHeaderReferenceState();
        resetItemForm();
        if (actionOptions.restoreDraft) {
            restoreDraftSession();
        } else {
            $scope.awbDrafts = [];
            $scope.activeAwbDraft = null;
        }
        $scope.$evalAsync(function () {
            $scope.suspendAutoNumberSync = false;
            $scope.suspendMeasuredWeightSync = false;
        });
    }

    function resetItemForm() {
        $scope.itemForm = createItemForm();
    }

    function readJsonStorage(storageRef, keyName) {
        var rawValue;

        if (!storageRef) {
            return {};
        }

        try {
            rawValue = storageRef.getItem(keyName);
        } catch (storageError) {
            return {};
        }

        if (!rawValue) {
            return {};
        }

        try {
            return JSON.parse(rawValue) || {};
        } catch (error) {
            return {};
        }
    }

    function writeJsonStorage(storageRef, keyName, payload) {
        var isEmptyObject;

        if (!storageRef) {
            return;
        }

        isEmptyObject = angular.isObject(payload) && !angular.isArray(payload) && !Object.keys(payload || {}).length;
        if (payload === undefined || payload === null || (angular.isArray(payload) && !payload.length) || isEmptyObject) {
            try {
                storageRef.removeItem(keyName);
            } catch (removeError) {
                return;
            }
            return;
        }

        try {
            storageRef.setItem(keyName, angular.toJson(payload));
        } catch (writeError) {
            return;
        }
    }

    function getDraftStoreName() {
        return 'bagging-awb-drafts-v1';
    }

    function getOfflineQueueStoreName() {
        return 'bagging-offline-queue-v1';
    }

    function getDraftSessionKey() {
        return normalizeTextValue($scope.headerForm.OriginalBagId || $scope.headerForm.BagId) || getCurrentNumberKey() || 'default';
    }

    function readDraftStore() {
        return angular.extend({}, readJsonStorage($window.localStorage, getDraftStoreName()), readJsonStorage($window.sessionStorage, getDraftStoreName()));
    }

    function writeDraftStore(payload) {
        writeJsonStorage($window.localStorage, getDraftStoreName(), payload || {});
        writeJsonStorage($window.sessionStorage, getDraftStoreName(), payload || {});
        $scope.offlineState.draftPersisted = !!Object.keys(payload || {}).length;
    }

    function readOfflineQueue() {
        var queue = readJsonStorage($window.localStorage, getOfflineQueueStoreName());

        return angular.isArray(queue) ? queue : [];
    }

    function writeOfflineQueue(queue) {
        writeJsonStorage($window.localStorage, getOfflineQueueStoreName(), angular.isArray(queue) ? queue : []);
        $scope.offlineState.queueCount = angular.isArray(queue) ? queue.length : 0;
    }

    function refreshOfflineState() {
        $scope.offlineState.isOnline = isClientOnline();
        $scope.offlineState.queueCount = readOfflineQueue().length;
        $scope.offlineState.draftPersisted = !!Object.keys(readDraftStore()).length;
    }

    function saveDraftSession() {
        var store = readDraftStore();
        var sessionKey = getDraftSessionKey();
        var hasBulkInput = !!normalizeTextValue($scope.itemForm.BulkAwbs);

        if (!$scope.awbDrafts.length && !hasBulkInput) {
            delete store[sessionKey];
            return writeDraftStore(store);
        }

        store[sessionKey] = {
            drafts: angular.copy($scope.awbDrafts),
            activeAwb: $scope.activeAwbDraft ? $scope.activeAwbDraft.awb : '',
            bulkAwbs: normalizeTextValue($scope.itemForm.BulkAwbs)
        };
        return writeDraftStore(store);
    }

    function clearDraftSession() {
        var store = readDraftStore();

        delete store[getDraftSessionKey()];
        writeDraftStore(store);
    }

    function findDraftIndex(awb) {
        var index = -1;
        var normalizedAwb = normalizeUpperValue(awb);

        angular.forEach($scope.awbDrafts, function (row, rowIndex) {
            if (index === -1 && normalizeUpperValue(row && row.awb) === normalizedAwb) {
                index = rowIndex;
            }
        });

        return index;
    }

    function findBaggingItemByAwb(awb) {
        var normalizedAwb = normalizeUpperValue(awb);
        var found = null;

        angular.forEach($scope.baggingDetail.items || [], function (row) {
            if (!found && normalizeUpperValue(row && row.awb) === normalizedAwb) {
                found = row;
            }
        });

        return found;
    }

    function buildShipmentLookupUrl(awb) {
        var query = [];

        if ($scope.headerForm.BagId) {
            query.push('bagId=' + encodeURIComponent($scope.headerForm.BagId));
        }
        if ($scope.headerForm.DestinationHostCode) {
            query.push('destinationHostCode=' + encodeURIComponent($scope.headerForm.DestinationHostCode));
        }

        return apiBase + '/awbs/' + encodeURIComponent(normalizeUpperValue(awb)) + (query.length ? '?' + query.join('&') : '');
    }

    function requestShipmentAwbDetail(awb, callback) {
        return urlServices.ambilData(buildShipmentLookupUrl(awb), {}, callback);
    }

    function requestShipmentAwbDetailsBulk(awbs, callback) {
        return urlServices.simpanData(apiBase + '/awbs-bulk', {
            AWBs: awbs,
            BagId: $scope.headerForm.BagId,
            DestinationHostCode: $scope.headerForm.DestinationHostCode
        }, callback);
    }

    function syncBulkAwbInputFromBaggingItems(items) {
        $scope.itemForm.BulkAwbs = (items || []).map(function (row) {
            return normalizeUpperValue(row && row.awb);
        }).filter(function (row) {
            return !!row;
        }).join('\n');
    }

    function mergeDraftRow(row) {
        var targetIndex;

        if (!row || !row.awb) {
            return;
        }

        targetIndex = findDraftIndex(row.awb);
        if (targetIndex >= 0) {
            $scope.awbDrafts[targetIndex] = angular.extend({}, $scope.awbDrafts[targetIndex], row);
            return;
        }

        $scope.awbDrafts.push(row);
    }

    function setActiveAwbDraft(awb, skipPersist) {
        var normalizedAwb = normalizeUpperValue(awb);

        $scope.activeAwbDraft = null;
        angular.forEach($scope.awbDrafts, function (row) {
            row.isActive = normalizeUpperValue(row && row.awb) === normalizedAwb;
            if (row.isActive) {
                $scope.activeAwbDraft = row;
            }
        });

        if (!skipPersist) {
            saveDraftSession();
        }
    }

    function sanitizeDraftRow(draft, syncWeight) {
        var requestedCollie;

        if (!draft) {
            return;
        }

        requestedCollie = normalizeIntegerValue(draft.collieBagging, 0);
        draft.validationErrorMessage = '';
        draft.totalCollie = normalizeIntegerValue(draft.totalCollie, 0);
        draft.availableCollie = normalizeIntegerValue(draft.availableCollie, draft.totalCollie);
        draft.remainingCollie = normalizeIntegerValue(draft.remainingCollie, draft.availableCollie);
        draft.collieBagging = requestedCollie;
        draft.totalKg = normalizeDecimalValue(draft.totalKg, 0);
        draft.totalVolume = normalizeDecimalValue(draft.totalVolume, 0);
        draft.totalVolumeWeight = normalizeDecimalValue(draft.totalVolumeWeight, 0);
        draft.lengthCm = normalizeDecimalValue(draft.lengthCm, 0);
        draft.widthCm = normalizeDecimalValue(draft.widthCm, 0);
        draft.heightCm = normalizeDecimalValue(draft.heightCm, 0);
        draft.weightBagging = normalizeDecimalValue(draft.weightBagging, 0);
        draft.actualWeight = normalizeDecimalValue(draft.actualWeight, draft.weightBagging);
        draft.packageType = normalizeTextValue(draft.packageType || $scope.headerForm.PackageType || 'KARUNG');
        draft.uom = normalizeUpperValue(draft.uom || 'KG') || 'KG';

        if (draft.collieBagging < 0) {
            draft.validationErrorMessage = 'Jumlah koli tidak boleh minus';
            draft.collieBagging = 0;
        }
        if (draft.availableCollie > 0 && requestedCollie > draft.availableCollie) {
            draft.validationErrorMessage = 'Jumlah koli melebihi sisa yang tersedia';
            draft.collieBagging = draft.availableCollie;
        }
        if (syncWeight) {
            draft.weightBagging = getProportionalWeight(draft.totalKg, draft.totalCollie, draft.collieBagging);
            draft.actualWeight = draft.weightBagging;
        }
    }

    function getDraftRemainingAfterSelection(row) {
        return Math.max(normalizeIntegerValue(row && row.availableCollie, 0) - normalizeIntegerValue(row && row.collieBagging, 0), 0);
    }

    function getDraftStatus(row) {
        if (!row) {
            return 'pending';
        }
        if (row.validationErrorMessage) {
            return 'error';
        }
        if (normalizeIntegerValue(row.collieBagging, 0) <= 0) {
            return 'pending';
        }
        if (getDraftRemainingAfterSelection(row) === 0) {
            return 'complete';
        }

        return 'pending';
    }

    function buildDraftProgressSummary() {
        var summary = {
            total: ($scope.awbDrafts || []).length,
            processed: 0,
            completed: 0,
            errors: 0,
            percent: 0
        };

        angular.forEach($scope.awbDrafts || [], function (row) {
            var status = getDraftStatus(row);

            if (status === 'error') {
                summary.errors += 1;
                return;
            }
            if (normalizeIntegerValue(row && row.collieBagging, 0) > 0) {
                summary.processed += 1;
            }
            if (status === 'complete') {
                summary.completed += 1;
            }
        });

        if (summary.total > 0) {
            summary.percent = Math.round((summary.processed / summary.total) * 100);
        }

        return summary;
    }

    function buildDraftFromShipment(detail) {
        var existingDraftIndex = findDraftIndex(detail && detail.awb);
        var existingDraft = existingDraftIndex >= 0 ? $scope.awbDrafts[existingDraftIndex] : null;
        var existingItem = findBaggingItemByAwb(detail && detail.awb);
        var snapshot = existingItem && angular.isObject(existingItem.data_barang) ? existingItem.data_barang : {};
        var row = {
            awb: normalizeUpperValue(detail && detail.awb),
            shipmentDateLabel: detail && detail.shipment_date_label ? detail.shipment_date_label : '',
            originCode: detail && detail.origin_code ? detail.origin_code : (snapshot.origin_code || ''),
            originName: detail && detail.origin_name ? detail.origin_name : (snapshot.origin_name || ''),
            destinationCode: detail && detail.destination_code ? detail.destination_code : (snapshot.destination_code || ''),
            destinationName: detail && detail.destination_name ? detail.destination_name : (snapshot.destination_name || ''),
            destHostCode: detail && detail.dest_host_code ? detail.dest_host_code : (snapshot.dest_host_code || ''),
            destHostName: detail && detail.dest_host_name ? detail.dest_host_name : (snapshot.dest_host_name || ''),
            shipperName: detail && detail.shipper_name ? detail.shipper_name : (snapshot.shipper_name || ''),
            receiverName: detail && detail.receiver_name ? detail.receiver_name : (snapshot.receiver_name || ''),
            itemDescription: detail && detail.item_description ? detail.item_description : (snapshot.item_description || ''),
            specialInstruction: detail && detail.special_instruction ? detail.special_instruction : (snapshot.special_instruction || ''),
            totalKg: existingDraft ? existingDraft.totalKg : normalizeDecimalValue(existingItem && existingItem.weight_total, normalizeDecimalValue(detail && detail.total_kg, 0)),
            totalVolume: normalizeDecimalValue(detail && detail.total_volume, 0),
            totalVolumeWeight: normalizeDecimalValue(detail && detail.total_volume_weight, 0),
            totalCollie: existingDraft ? existingDraft.totalCollie : normalizeIntegerValue(existingItem && existingItem.collie_total, normalizeIntegerValue(detail && detail.total_collie, 0)),
            availableCollie: normalizeIntegerValue(detail && detail.available_collie, 0),
            remainingCollie: normalizeIntegerValue(detail && detail.remaining_collie, 0),
            allocatedCollie: normalizeIntegerValue(detail && detail.allocated_collie, 0),
            collieBagging: existingDraft ? existingDraft.collieBagging : normalizeIntegerValue(existingItem && existingItem.collie_bagging, normalizeIntegerValue(detail && detail.suggested_collie, 0)),
            weightBagging: existingDraft ? existingDraft.weightBagging : normalizeDecimalValue(existingItem && existingItem.weight_bagging, normalizeDecimalValue(detail && detail.suggested_weight_bagging, 0)),
            actualWeight: existingDraft ? existingDraft.actualWeight : normalizeDecimalValue(existingItem && existingItem.actual_weight, normalizeDecimalValue(detail && detail.suggested_weight_bagging, 0)),
            lengthCm: existingDraft ? existingDraft.lengthCm : normalizeDecimalValue(existingItem && existingItem.length_cm, normalizeDecimalValue(detail && detail.length_cm, 0)),
            widthCm: existingDraft ? existingDraft.widthCm : normalizeDecimalValue(existingItem && existingItem.width_cm, normalizeDecimalValue(detail && detail.width_cm, 0)),
            heightCm: existingDraft ? existingDraft.heightCm : normalizeDecimalValue(existingItem && existingItem.height_cm, normalizeDecimalValue(detail && detail.height_cm, 0)),
            packageType: existingDraft ? existingDraft.packageType : ((existingItem && existingItem.package_type) || $scope.headerForm.PackageType || 'KARUNG'),
            note: existingDraft ? existingDraft.note : ((existingItem && existingItem.note) || ''),
            uom: existingDraft ? existingDraft.uom : ((existingItem && existingItem.uom) || (detail && detail.uom) || 'KG'),
            baggingAllocations: detail && angular.isArray(detail.bagging_allocations) ? detail.bagging_allocations : [],
            availabilityMessage: detail && detail.availability_message ? detail.availability_message : '',
            isExistingItem: !!existingItem
        };

        sanitizeDraftRow(row, false);
        return row;
    }

    function buildDraftFromBaggingItem(item) {
        var snapshot = item && angular.isObject(item.data_barang) ? item.data_barang : parseExtraPayload(item && item.data_barang);
        var row = {
            awb: normalizeUpperValue(item && item.awb),
            shipmentDateLabel: normalizeTextValue(snapshot && snapshot.shipment_date_label),
            originCode: normalizeUpperValue(snapshot && snapshot.origin_code),
            originName: normalizeTextValue(snapshot && snapshot.origin_name),
            destinationCode: normalizeUpperValue(snapshot && snapshot.destination_code),
            destinationName: normalizeTextValue(snapshot && snapshot.destination_name),
            destHostCode: normalizeUpperValue((item && item.dest_host_code) || (snapshot && snapshot.dest_host_code)),
            destHostName: normalizeTextValue((item && item.dest_host_name) || (snapshot && snapshot.dest_host_name)),
            shipperName: normalizeTextValue(snapshot && snapshot.shipper_name),
            receiverName: normalizeTextValue(snapshot && snapshot.receiver_name),
            itemDescription: normalizeTextValue(snapshot && snapshot.item_description),
            specialInstruction: normalizeTextValue(snapshot && snapshot.special_instruction),
            totalKg: normalizeDecimalValue(item && item.weight_total, normalizeDecimalValue(snapshot && snapshot.total_kg, 0)),
            totalVolume: normalizeDecimalValue(snapshot && snapshot.total_volume, 0),
            totalVolumeWeight: normalizeDecimalValue(item && item.volume_weight, normalizeDecimalValue(snapshot && snapshot.total_volume_weight, 0)),
            totalCollie: normalizeIntegerValue(item && item.collie_total, normalizeIntegerValue(snapshot && snapshot.total_collie, 0)),
            availableCollie: normalizeIntegerValue(snapshot && snapshot.remaining_collie_after_save, normalizeIntegerValue(item && item.collie_bagging, 0)),
            remainingCollie: normalizeIntegerValue(snapshot && snapshot.remaining_collie_after_save, normalizeIntegerValue(item && item.collie_bagging, 0)),
            allocatedCollie: normalizeIntegerValue(snapshot && snapshot.allocated_collie_before_save, normalizeIntegerValue(item && item.collie_bagging, 0)),
            collieBagging: normalizeIntegerValue(item && item.collie_bagging, 0),
            weightBagging: normalizeDecimalValue(item && item.weight_bagging, 0),
            actualWeight: normalizeDecimalValue(item && item.actual_weight, normalizeDecimalValue(item && item.weight_bagging, 0)),
            lengthCm: normalizeDecimalValue(item && item.length_cm, normalizeDecimalValue(snapshot && snapshot.dimensi && snapshot.dimensi.panjang, 0)),
            widthCm: normalizeDecimalValue(item && item.width_cm, normalizeDecimalValue(snapshot && snapshot.dimensi && snapshot.dimensi.lebar, 0)),
            heightCm: normalizeDecimalValue(item && item.height_cm, normalizeDecimalValue(snapshot && snapshot.dimensi && snapshot.dimensi.tinggi, 0)),
            packageType: normalizeTextValue(item && item.package_type),
            note: normalizeTextValue(item && item.note),
            uom: normalizeUpperValue(item && item.uom) || 'KG',
            baggingAllocations: [],
            availabilityMessage: '',
            isExistingItem: true
        };

        sanitizeDraftRow(row, false);
        return row;
    }

    function restoreDraftSession() {
        var store = readDraftStore();
        var payload = store[getDraftSessionKey()];

        $scope.awbDrafts = payload && angular.isArray(payload.drafts) ? payload.drafts : [];
        $scope.itemForm.BulkAwbs = payload && payload.bulkAwbs ? payload.bulkAwbs : ($scope.itemForm.BulkAwbs || '');
        angular.forEach($scope.awbDrafts, function (row) {
            sanitizeDraftRow(row, false);
        });

        if ($scope.awbDrafts.length) {
            setActiveAwbDraft((payload && payload.activeAwb) || $scope.awbDrafts[0].awb, true);
        } else {
            $scope.activeAwbDraft = null;
        }
    }

    function checkDuplicateAwbInOtherBaggings(detail) {
        var currentBagId = normalizeUpperValue($scope.headerForm.BagId);
        var allocations = detail && angular.isArray(detail.bagging_allocations) ? detail.bagging_allocations : [];
        var otherBaggings = [];
        
        angular.forEach(allocations, function(alloc) {
            var allocBagId = normalizeUpperValue(alloc && alloc.bag_id);
            if (allocBagId && allocBagId !== currentBagId) {
                otherBaggings.push({
                    bagId: alloc.bag_id,
                    collie: normalizeIntegerValue(alloc.collie_bagging, 0),
                    weight: normalizeDecimalValue(alloc.weight_bagging, 0),
                    updatedAt: alloc.updated_at || ''
                });
            }
        });
        
        return otherBaggings;
    }
    
    function buildDuplicateWarningMessage(awb, otherBaggings) {
        if (!otherBaggings || !otherBaggings.length) {
            return '';
        }
        
        var message = 'AWB ' + awb + ' sudah ada di bagging lain:\n';
        angular.forEach(otherBaggings, function(bag, index) {
            message += '\n' + (index + 1) + '. Bagging ' + bag.bagId +
                      ' (Koli: ' + bag.collie +
                      ', Berat: ' + bag.weight + ' kg)';
        });
        message += '\n\nApakah Anda yakin ingin menambahkan AWB ini ke bagging saat ini?';
        
        return message;
    }
    
    function upsertDraftFromShipment(detail, silentMode) {
        var otherBaggings = checkDuplicateAwbInOtherBaggings(detail);
        var awb = normalizeUpperValue(detail && detail.awb);
        
        if (otherBaggings.length > 0) {
            var warningMessage = buildDuplicateWarningMessage(awb, otherBaggings);
            showConfirm({
                headerText: 'Peringatan AWB Duplikat',
                bodyText: warningMessage
            }, function() {
                var row = buildDraftFromShipment(detail);
                mergeDraftRow(row);
                setActiveAwbDraft(row.awb, true);
                saveDraftSession();
                
                if (!silentMode) {
                    toaster.pop('success', 'Bagging', 'AWB ' + row.awb + ' berhasil dimuat ke daftar bagging');
                }
            });
            return;
        }
        
        var row = buildDraftFromShipment(detail);
        mergeDraftRow(row);
        setActiveAwbDraft(row.awb, true);
        saveDraftSession();

        if (!silentMode) {
            toaster.pop('success', 'Bagging', 'AWB ' + row.awb + ' berhasil dimuat ke daftar bagging');
        }
    }

    function clearDraftState(skipSessionClear) {
        $scope.itemForm.BulkAwbs = '';
        $scope.awbDrafts = [];
        $scope.activeAwbDraft = null;

        if (!skipSessionClear) {
            clearDraftSession();
        }

        refreshOfflineState();
    }

    function validateDraftRows() {
        var validationMessage = '';

        if (!$scope.awbDrafts.length) {
            return 'Tambahkan minimal satu AWB ke daftar bagging';
        }

        angular.forEach($scope.awbDrafts, function (row) {
            sanitizeDraftRow(row, false);
            if (validationMessage) {
                return;
            }
            if (!row.awb) {
                validationMessage = 'Nomor AWB pada draft tidak valid';
                return;
            }
            if (row.collieBagging <= 0) {
                validationMessage = 'Jumlah koli yang diambil untuk AWB ' + row.awb + ' harus lebih besar dari 0';
                return;
            }
            if (row.availableCollie > 0 && row.collieBagging > row.availableCollie) {
                validationMessage = 'Jumlah koli AWB ' + row.awb + ' melebihi sisa yang tersedia (' + row.availableCollie + ')';
            }
        });

        return validationMessage;
    }

    function hydrateDraftRowsFromExistingItems(items) {
        $scope.awbDrafts = [];

        angular.forEach(items || [], function (item) {
            var row = buildDraftFromBaggingItem(item);

            if (row && row.awb) {
                $scope.awbDrafts.push(row);
            }
        });

        if ($scope.awbDrafts.length) {
            setActiveAwbDraft($scope.awbDrafts[0].awb, true);
        } else {
            $scope.activeAwbDraft = null;
        }

        saveDraftSession();
    }

    function refreshDraftRowsFromShipment(items, callback) {
        var awbList = (items || []).map(function (row) {
            return normalizeUpperValue(row && row.awb);
        }).filter(function (row) {
            return !!row;
        });

        if (!awbList.length) {
            if (callback) {
                callback(null, {
                    rows: [],
                    failed: []
                });
            }
            return;
        }

        return requestShipmentAwbDetailsBulk(awbList, function (err, summary) {
            if (!err) {
                angular.forEach((summary && summary.rows) || [], function (row) {
                    mergeDraftRow(buildDraftFromShipment(row));
                });

                if (!$scope.activeAwbDraft && $scope.awbDrafts.length) {
                    setActiveAwbDraft($scope.awbDrafts[0].awb, true);
                }

                saveDraftSession();
            }

            if (callback) {
                callback(err, summary);
            }
        });
    }

    function buildOfflineQueueSignature(entry) {
        var snapshot = entry && entry.state ? entry.state : {};
        var header = snapshot.headerForm || {};
        var item = snapshot.itemForm || {};

        return [
            normalizeTextValue(entry && entry.action),
            normalizeUpperValue(header.BagId || header.OriginalBagId),
            normalizeUpperValue(item.BulkAwbs),
            (snapshot.awbDrafts || []).length
        ].join('|');
    }

    function captureWorkingSnapshot() {
        return {
            headerForm: angular.copy($scope.headerForm),
            itemForm: angular.copy($scope.itemForm),
            awbDrafts: angular.copy($scope.awbDrafts),
            activeAwb: $scope.activeAwbDraft ? $scope.activeAwbDraft.awb : '',
            baggingDetail: angular.copy($scope.baggingDetail),
            searchBagId: $scope.searchBagId || ''
        };
    }

    function applyWorkingSnapshot(snapshot) {
        var targetSnapshot = snapshot || {};

        $scope.suspendMeasuredWeightSync = true;
        $scope.headerForm = angular.extend(createHeaderForm(), angular.copy(targetSnapshot.headerForm || {}));
        $scope.itemForm = angular.extend(createItemForm(), angular.copy(targetSnapshot.itemForm || {}));
        $scope.awbDrafts = angular.copy(targetSnapshot.awbDrafts || []);
        $scope.baggingDetail = angular.copy(targetSnapshot.baggingDetail || {
            header: null,
            items: [],
            summaryByDestination: [],
            summaryByCity: []
        });
        $scope.searchBagId = normalizeTextValue(targetSnapshot.searchBagId || $scope.headerForm.BagId);
        $scope.suspendAutoNumberSync = true;
        syncManifestDateValue($scope.headerForm.ManifestDate || moment());
        angular.forEach($scope.awbDrafts, function (row) {
            sanitizeDraftRow(row, false);
        });
        syncHeaderReferenceState();
        if ($scope.awbDrafts.length) {
            setActiveAwbDraft(targetSnapshot.activeAwb || $scope.awbDrafts[0].awb, true);
        } else {
            $scope.activeAwbDraft = null;
        }
        $scope.$evalAsync(function () {
            $scope.suspendAutoNumberSync = false;
            $scope.suspendMeasuredWeightSync = false;
        });
    }

    function queueOfflineAction(actionType, toastMessage) {
        return toaster.pop('warning', 'Bagging', 'Koneksi sedang offline atau terputus. Silakan periksa jaringan internet Anda.');
    }

    function processOfflineQueueEntry(entry, callback) {
        var previousSnapshot = captureWorkingSnapshot();

        function finish(err, result) {
            applyWorkingSnapshot(previousSnapshot);
            if (callback) {
                callback(err, result);
            }
        }

        applyWorkingSnapshot(entry && entry.state ? entry.state : {});

        if (entry.action === 'save-header') {
            return maybeGenerateBaggingNumber(false, function (numberErr) {
                if (numberErr) {
                    return finish(numberErr);
                }

                return executeSaveHeader({
                    showError: false,
                    showSuccess: false,
                    reloadDetail: false
                }, finish);
            });
        }

        if (entry.action === 'save' || entry.action === 'save-and-confirm') {
            return maybeGenerateBaggingNumber(false, function (numberErr) {
                if (numberErr) {
                    return finish(numberErr);
                }

                return executeSaveHeader({
                    showError: false,
                    showSuccess: false,
                    reloadDetail: false
                }, function (saveHeaderErr) {
                    if (saveHeaderErr) {
                        return finish(saveHeaderErr);
                    }

                    return saveDraftRowsSequentially(angular.copy($scope.awbDrafts), 0, function (saveItemsErr, saveItemsResult) {
                        if (saveItemsErr) {
                            return finish(saveItemsErr);
                        }

                        clearDraftState();
                        return finish(null, saveItemsResult);
                    });
                });
            });
        }

        return finish('Tipe sinkronisasi offline tidak dikenali');
    }

    function syncOfflineQueue() {
        var queue = readOfflineQueue();

        refreshOfflineState();
        if (!isClientOnline() || $scope.offlineState.syncing || !queue.length) {
            return;
        }

        $scope.offlineState.syncing = true;
        return processOfflineQueueEntry(queue[0], function (err) {
            var currentQueue = readOfflineQueue();

            $scope.offlineState.syncing = false;
            if (err) {
                refreshOfflineState();
                if (!isOfflineLikeError(err)) {
                    toaster.pop('error', 'Bagging', 'Sinkronisasi offline gagal: ' + normalizeError(err));
                    triggerHapticFeedback('error');
                }
                return;
            }

            currentQueue.shift();
            writeOfflineQueue(currentQueue);
            $scope.offlineState.lastSyncedAt = moment().format('HH:mm:ss');
            refreshOfflineState();
            toaster.pop('success', 'Bagging', '1 antrean offline berhasil disinkronkan');
            triggerHapticFeedback('success');

            if (currentQueue.length) {
                return syncOfflineQueue();
            }
        });
    }

    function buildDraftPayload(row) {
        return {
            AWB: row.awb,
            CollieTotal: row.totalCollie,
            CollieBagging: row.collieBagging,
            WeightTotal: row.totalKg,
            WeightBagging: row.weightBagging,
            ActualWeight: row.actualWeight,
            LengthCm: row.lengthCm,
            WidthCm: row.widthCm,
            HeightCm: row.heightCm,
            PackageType: row.packageType || $scope.headerForm.PackageType,
            Note: row.note || '',
            UOM: row.uom || 'KG'
        };
    }

    function saveDraftRowsSequentially(rows, index, callback) {
        var targetRows = rows || [];
        var currentIndex = index || 0;

        if (currentIndex >= targetRows.length) {
            return callback(null, {
                totalInput: targetRows.length
            });
        }

        return urlServices.simpanData(apiBase + '/' + encodeURIComponent($scope.headerForm.BagId) + '/items', buildDraftPayload(targetRows[currentIndex]), function (err) {
            if (err) {
                return callback('AWB ' + targetRows[currentIndex].awb + ': ' + normalizeError(err));
            }

            return saveDraftRowsSequentially(targetRows, currentIndex + 1, callback);
        });
    }

    function loadReferenceMasters(callback) {
        $scope.masterLoading = true;

        return urlServices.ambilData(apiBase + '/masters', {}, function (err, result) {
            $scope.masterLoading = false;
            if (err) {
                if (callback) {
                    callback(err);
                }
                return toaster.pop('error', 'Bagging', normalizeError(err));
            }

            $scope.referenceMasters = result || {
                hubs: [],
                destinations: []
            };
            syncHeaderReferenceState();
            syncOfflineQueue();
            if (callback) {
                return callback(null, $scope.referenceMasters);
            }
        });
    }

    function loadBaggingDetail(bagId, callback, options) {
        var actionOptions = angular.extend({
            clearOnError: false,
            updateUrl: false,
            preserveSearchValue: true,
            silent: false
        }, options || {});
        var targetBagId = normalizeTextValue(bagId || $scope.headerForm.BagId);

        if (!targetBagId) {
            if (callback) {
                callback('Masukkan nomor bagging terlebih dahulu');
            }
            if (!actionOptions.silent) {
                return toaster.pop('warning', 'Bagging', 'Masukkan nomor bagging terlebih dahulu');
            }
            return;
        }

        $scope.loading = true;
        return urlServices.ambilData(apiBase + '/' + encodeURIComponent(targetBagId), {}, function (err, result) {
            $scope.loading = false;
            if (err) {
                if (actionOptions.clearOnError) {
                    resetLoadedBaggingState({
                        preserveSearchValue: actionOptions.preserveSearchValue ? targetBagId : ''
                    });
                }
                if (callback) {
                    callback(err);
                }
                setSaveFeedback('error', normalizeError(err));
                if (!actionOptions.silent) {
                    return toaster.pop('error', 'Bagging', normalizeError(err));
                }
                return;
            }

            $scope.baggingDetail = result || {
                header: null,
                items: [],
                summaryByDestination: [],
                summaryByCity: []
            };
            mapHeaderToForm($scope.baggingDetail.header, {
                restoreDraft: false
            });
            if (actionOptions.updateUrl && $window.history && $window.history.replaceState) {
                $window.history.replaceState({}, $window.document ? $window.document.title : '', '/bagging?bagId=' + encodeURIComponent(targetBagId));
            }
            syncBulkAwbInputFromBaggingItems($scope.baggingDetail.items);
            hydrateDraftRowsFromExistingItems($scope.baggingDetail.items);
            if ($scope.baggingDetail.recoveredFromAudit) {
                setSaveFeedback('warning', 'Data AWB dipulihkan dari audit karena detail aktif tidak ditemukan. Silakan cek lalu simpan ulang.');
            } else {
                setSaveFeedback('', '');
            }

            return refreshDraftRowsFromShipment($scope.baggingDetail.items, function () {
                if (callback) {
                    return callback(null, $scope.baggingDetail);
                }
            });
        });
    }

    function executeSaveHeader(options, callback) {
        var actionOptions = angular.extend({
            showError: true,
            showSuccess: true,
            reloadDetail: true,
            successMessage: 'Header bagging berhasil disimpan'
        }, options || {});
        var payload = angular.copy($scope.headerForm);

        payload.BagNumber = payload.BagId || '';
        $scope.savingHeader = true;
        setSaveFeedback('info', 'Menyimpan header bagging...');

        return urlServices.simpanData(apiBase, payload, function (err, result) {
            $scope.savingHeader = false;
            if (err) {
                if (actionOptions.showError) {
                    toaster.pop('error', 'Bagging', normalizeError(err));
                }
                setSaveFeedback('error', normalizeError(err));
                if (callback) {
                    return callback(err);
                }
                return;
            }

            mapHeaderToForm(result);
            if (actionOptions.reloadDetail) {
                loadBaggingDetail(result && result.bag_id ? result.bag_id : $scope.headerForm.BagId);
            }
            if (actionOptions.showSuccess) {
                toaster.pop('success', 'Bagging', actionOptions.successMessage);
            }
            setSaveFeedback('success', actionOptions.successMessage || 'Header bagging berhasil disimpan');
            if (callback) {
                return callback(null, result);
            }
        });
    }

    function executeSaveBulkItems(options, callback) {
        var actionOptions = angular.extend({
            allowEmpty: false,
            showError: true,
            showSuccess: true,
            reloadDetail: true,
            successMessage: ''
        }, options || {});
        var awbList = parseBulkAwbList($scope.itemForm.BulkAwbs);
        var payload;

        if (!$scope.headerForm.BagId) {
            if (actionOptions.showError) {
                toaster.pop('warning', 'Bagging', 'Simpan header bagging terlebih dahulu');
            }
            if (callback) {
                return callback('Simpan header bagging terlebih dahulu');
            }
            return;
        }
        if (!awbList.length) {
            if (actionOptions.allowEmpty) {
                if (callback) {
                    return callback(null, {
                        totalInput: 0,
                        skipped: true
                    });
                }
                return;
            }

            if (actionOptions.showError) {
                toaster.pop('warning', 'Bagging', 'Isi daftar AWB bulk terlebih dahulu');
            }
            if (callback) {
                return callback('Isi daftar AWB bulk terlebih dahulu');
            }
            return;
        }

        payload = angular.copy($scope.itemForm);
        payload.AWBs = awbList;
        payload.PackageType = payload.PackageType || $scope.headerForm.PackageType;

        $scope.savingItem = true;
        return urlServices.simpanData(apiBase + '/' + encodeURIComponent($scope.headerForm.BagId) + '/items-bulk', payload, function (err, result) {
            $scope.savingItem = false;
            if (err) {
                if (actionOptions.showError) {
                    toaster.pop('error', 'Bagging', normalizeError(err));
                }
                setSaveFeedback('error', normalizeError(err));
                if (callback) {
                    return callback(err);
                }
                return;
            }

            if (actionOptions.reloadDetail) {
                loadBaggingDetail($scope.headerForm.BagId);
            }
            resetItemForm();
            if (actionOptions.showSuccess) {
                toaster.pop('success', 'Bagging', actionOptions.successMessage || ((result && result.totalInput ? result.totalInput : awbList.length) + ' AWB berhasil disimpan ke bagging'));
            }
            setSaveFeedback('success', actionOptions.successMessage || ((result && result.totalInput ? result.totalInput : awbList.length) + ' AWB berhasil disimpan ke bagging'));
            if (callback) {
                return callback(null, result);
            }
        });
    }

    function executeSaveAwbSelections(confirmAfterSave) {
        var validationMessage = validateDraftRows();
        var confirmationText = 'Simpan item AWB ini ke bagging?';

        if (!$scope.headerForm.HostCode) {
            setSaveFeedback('warning', 'Host code wajib dipilih');
            return toaster.pop('warning', 'Bagging', 'Host code wajib dipilih');
        }
        if (!$scope.headerForm.DestinationHostCode) {
            setSaveFeedback('warning', 'Destination host code wajib dipilih');
            return toaster.pop('warning', 'Bagging', 'Destination host code wajib dipilih');
        }
        if (validationMessage) {
            setSaveFeedback('warning', validationMessage);
            return toaster.pop('warning', 'Bagging', validationMessage);
        }

        showConfirm({
            headerText: 'Simpan Item Bagging',
            bodyText: confirmationText
        }, function () {
            if (!isClientOnline()) {
                return toaster.pop('warning', 'Bagging', 'Gagal menyimpan. Koneksi sedang offline.');
            }

            return maybeGenerateBaggingNumber(false, function (numberErr) {
                if (numberErr) {
                    setSaveFeedback('error', normalizeError(numberErr));
                    return toaster.pop('error', 'Bagging', normalizeError(numberErr));
                }

                return executeSaveHeader({
                    showError: false,
                    showSuccess: false
                }, function (saveHeaderErr) {
                    if (saveHeaderErr) {
                        setSaveFeedback('error', normalizeError(saveHeaderErr));
                        return toaster.pop('error', 'Bagging', normalizeError(saveHeaderErr));
                    }

                    $scope.savingSelections = true;
                    return saveDraftRowsSequentially(angular.copy($scope.awbDrafts), 0, function (saveItemsErr, saveItemsResult) {
                        $scope.savingSelections = false;

                        if (saveItemsErr) {
                            setSaveFeedback('error', saveItemsErr);
                            return toaster.pop('error', 'Bagging', saveItemsErr);
                        }

                        clearDraftState();
                        return loadBaggingDetail($scope.headerForm.BagId, function (loadErr) {
                            if (loadErr) {
                                setSaveFeedback('error', normalizeError(loadErr));
                                return toaster.pop('error', 'Bagging', normalizeError(loadErr));
                            }

                            setSaveFeedback('success', (saveItemsResult && saveItemsResult.totalInput ? saveItemsResult.totalInput : 0) + ' AWB berhasil disimpan ke bagging');
                            return toaster.pop('success', 'Bagging', (saveItemsResult && saveItemsResult.totalInput ? saveItemsResult.totalInput : 0) + ' AWB berhasil disimpan ke bagging');
                        });
                    });
                });
            });
        });
    }

    $scope.onHostCodeChange = function () {
        syncHeaderReferenceState();
        maybeGenerateBaggingNumber(false);
    };

    $scope.onDestinationHostCodeChange = function () {
        syncHeaderReferenceState();
        maybeGenerateBaggingNumber(false);
    };

    $scope.onPackageTypeChange = function () {
        if (!$scope.itemForm.PackageType) {
            $scope.itemForm.PackageType = $scope.headerForm.PackageType || 'KARUNG';
        }
    };

    $scope.$watch('manifestDateModel', function (newValue, oldValue) {
        if (!newValue) {
            return;
        }

        $scope.headerForm.ManifestDate = normalizeDatePickerValue(newValue).format('YYYY-MM-DD');
        if (oldValue && !$scope.suspendAutoNumberSync) {
            maybeGenerateBaggingNumber(false);
        }
    });

    $scope.$watchGroup(['headerForm.LengthCm', 'headerForm.WidthCm', 'headerForm.HeightCm', 'headerForm.ModTrans'], function () {
        syncHeaderVolumeActual();
    });

    $scope.showOriginLookup = function () {
        return openLookup('baggingHubList', {
            lookupType: 'origin'
        });
    };

    $scope.showDestinationLookup = function () {
        return openLookup('baggingHubList', {
            lookupType: 'destination'
        });
    };

    $scope.showAgentLookup = function () {
        return openLookup('baggingAgentList', {});
    };

    $scope.openBaggingLookup = function () {
        return openLookup('baggingEntryList', {});
    };

    $scope.loadBulkAwbDetails = function () {
        var awbList = parseBulkAwbList($scope.itemForm.BulkAwbs);

        if (!$scope.headerForm.DestinationHostCode) {
            return toaster.pop('warning', 'Bagging', 'Pilih destination host terlebih dahulu sebelum ambil detail AWB');
        }
        if (!awbList.length) {
            return toaster.pop('warning', 'Bagging', 'Scan atau isi daftar AWB terlebih dahulu');
        }

        $scope.awbDetailLoading = true;
        return requestShipmentAwbDetailsBulk(awbList, function (err, summary) {
            var failedMessages;
            var failedMessage;

            $scope.awbDetailLoading = false;
            if (err) {
                return toaster.pop('error', 'Bagging', normalizeError(err));
            }

            angular.forEach((summary && summary.rows) || [], function (row) {
                upsertDraftFromShipment(row, true);
            });

            failedMessages = (summary.failed || []).map(function (row) {
                return row.message || row.awb;
            });

            if (summary.loaded) {
                toaster.pop('success', 'Bagging', summary.loaded + ' AWB berhasil dimuat ke daftar detail');
                triggerHapticFeedback('success');
            }
            if (failedMessages.length) {
                failedMessage = failedMessages.slice(0, 2).join(' | ');
                if (failedMessages.length > 2) {
                    failedMessage += ' | dan ' + (failedMessages.length - 2) + ' AWB lain';
                }
                toaster.pop('warning', 'Bagging', failedMessage);
                triggerHapticFeedback('error');
            }
        });
    };

    $scope.selectAwbDraft = function (row) {
        if (!row || !row.awb) {
            return true;
        }

        setActiveAwbDraft(row.awb);
        return true;
    };

    $scope.removeAwbDraft = function (row) {
        var targetIndex;

        if (!row || !row.awb) {
            return true;
        }

        targetIndex = findDraftIndex(row.awb);
        if (targetIndex < 0) {
            return true;
        }

        $scope.awbDrafts.splice(targetIndex, 1);
        if ($scope.activeAwbDraft && normalizeUpperValue($scope.activeAwbDraft.awb) === normalizeUpperValue(row.awb)) {
            if ($scope.awbDrafts.length) {
                setActiveAwbDraft($scope.awbDrafts[0].awb, true);
            } else {
                $scope.activeAwbDraft = null;
            }
        }
        saveDraftSession();
        return true;
    };

    $scope.handleDraftFieldChange = function (row) {
        var targetRow = row || $scope.activeAwbDraft;

        if (targetRow) {
            sanitizeDraftRow(targetRow, false);
            if (targetRow.validationErrorMessage) {
                triggerHapticFeedback('error');
            }
            saveDraftSession();
        }
    };

    $scope.handleDraftCollieChange = function (row) {
        var targetRow = row || $scope.activeAwbDraft;

        if (targetRow) {
            sanitizeDraftRow(targetRow, true);
            if (targetRow.validationErrorMessage) {
                triggerHapticFeedback('error');
            }
            saveDraftSession();
        }
    };

    $scope.handleBulkAwbInputChange = function () {
        saveDraftSession();
    };

    $scope.incrementDraftCollie = function (row) {
        var targetRow = row || $scope.activeAwbDraft;
        var maxCollie;

        if (!targetRow) {
            return true;
        }

        maxCollie = normalizeIntegerValue(targetRow.availableCollie, normalizeIntegerValue(targetRow.totalCollie, 0));
        if (maxCollie > 0 && normalizeIntegerValue(targetRow.collieBagging, 0) >= maxCollie) {
            targetRow.validationErrorMessage = 'Jumlah koli sudah mencapai sisa maksimum';
            triggerHapticFeedback('warning');
            return true;
        }

        targetRow.collieBagging = normalizeIntegerValue(targetRow.collieBagging, 0) + 1;
        return $scope.handleDraftCollieChange(targetRow);
    };

    $scope.decrementDraftCollie = function (row) {
        var targetRow = row || $scope.activeAwbDraft;

        if (!targetRow) {
            return true;
        }

        targetRow.collieBagging = Math.max(normalizeIntegerValue(targetRow.collieBagging, 0) - 1, 0);
        return $scope.handleDraftCollieChange(targetRow);
    };

    $scope.getDraftStatus = function (row) {
        return getDraftStatus(row);
    };

    $scope.getDraftStatusLabel = function (row) {
        var status = getDraftStatus(row);

        if (status === 'error') {
            return row && row.validationErrorMessage ? row.validationErrorMessage : 'Periksa input koli';
        }
        if (status === 'complete') {
            return 'Selesai, sisa koli 0';
        }
        if (normalizeIntegerValue(row && row.collieBagging, 0) > 0) {
            return 'Sudah dipilih, sisa ' + getDraftRemainingAfterSelection(row);
        }

        return 'Belum diproses';
    };

    $scope.getDraftRemainingAfterSelection = function (row) {
        return getDraftRemainingAfterSelection(row);
    };

    $scope.getDraftProgress = function () {
        return buildDraftProgressSummary();
    };

    $scope.enableHeaderMeasuredWeightAuto = function () {
        $scope.headerMeasuredWeightAuto = true;
        syncHeaderMeasuredWeight();
    };

    $scope.disableHeaderMeasuredWeightAuto = function () {
        $scope.headerMeasuredWeightAuto = false;
    };

    $scope.onMeasuredWeightManualInput = function () {
        if ($scope.headerMeasuredWeightAuto) {
            $scope.headerMeasuredWeightAuto = false;
        }
    };


    $scope.getCurrentBaggingLockMessage = function () {
        return 'Bagging yang AWB-nya sudah termanifest tidak bisa diubah atau dihapus';
    };

    $scope.hasManifestedBaggingItems = function () {
        var header = $scope.baggingDetail && $scope.baggingDetail.header;

        if (header && (normalizeBooleanValue(header.has_manifested_awb) || normalizeIntegerValue(header.manifested_awb_count, 0) > 0)) {
            return true;
        }

        return ($scope.baggingDetail.items || []).some(function (item) {
            return normalizeBooleanValue(item && item.is_manifest);
        });
    };

    $scope.isCurrentBaggingLocked = function () {
        return $scope.hasManifestedBaggingItems();
    };

    $scope.canDeleteCurrentBagging = function () {
        return !!($scope.headerForm.BagId && $scope.baggingDetail && $scope.baggingDetail.header && !$scope.hasManifestedBaggingItems());
    };

    $scope.startCameraScan = function () {
        var barcodeDetector = getBarcodeDetectorInstance();

        if (!$scope.cameraScanner.supported || !$window.navigator || !$window.navigator.mediaDevices || !$window.navigator.mediaDevices.getUserMedia || !barcodeDetector) {
            $scope.cameraScanner.error = 'Browser HP ini belum mendukung scan kamera langsung. Gunakan scanner hardware atau paste AWB.';
            toaster.pop('warning', 'Bagging', $scope.cameraScanner.error);
            triggerHapticFeedback('error');
            return;
        }

        cameraLastSeenMap = {};
        $scope.cameraScanner.error = '';
        $scope.cameraScanner.lastMessage = 'Arahkan kamera ke barcode AWB';
        $scope.cameraScanner.loading = true;

        return $window.navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: {
                    ideal: 'environment'
                }
            },
            audio: false
        }).then(function (stream) {
            var videoElement;

            activeCameraStream = stream;
            $scope.$evalAsync(function () {
                $scope.cameraScanner.active = true;
                $scope.cameraScanner.loading = false;
            });

            return $window.setTimeout(function () {
                videoElement = getCameraVideoElement();
                if (!videoElement) {
                    return;
                }

                videoElement.srcObject = stream;
                videoElement.setAttribute('playsinline', 'true');
                videoElement.play();
                scheduleCameraFrameScan();
            }, 0);
        }).catch(function (cameraError) {
            $scope.$evalAsync(function () {
                $scope.cameraScanner.loading = false;
                $scope.cameraScanner.active = false;
                $scope.cameraScanner.error = 'Kamera tidak bisa dibuka: ' + normalizeError(cameraError);
            });
            toaster.pop('error', 'Bagging', 'Kamera tidak bisa dibuka untuk scan AWB');
            triggerHapticFeedback('error');
        });
    };

    $scope.stopCameraScan = function () {
        stopCameraScan(false);
    };

    $scope.removeOrDeleteAwbRow = function (row) {
        if ($scope.isCurrentBaggingLocked()) {
            toaster.pop('warning', 'Bagging', $scope.getCurrentBaggingLockMessage());
            return true;
        }
        if (row && row.isExistingItem && $scope.headerForm.BagId && $scope.baggingDetail && $scope.baggingDetail.header && !$scope.hasManifestedBaggingItems()) {
            return $scope.deleteItem(row);
        }

        return $scope.removeAwbDraft(row);
    };

    $scope.clearAwbDrafts = function () {
        if (!$scope.awbDrafts.length) {
            return clearDraftState();
        }

        showConfirm({
            headerText: 'Bersihkan Daftar AWB',
            bodyText: 'Hapus semua AWB yang belum disimpan dari daftar ini?'
        }, function () {
            clearDraftState();
        });
    };

    $scope.saveAwbSelections = function () {
        if ($scope.isCurrentBaggingLocked()) {
            return toaster.pop('warning', 'Bagging', $scope.getCurrentBaggingLockMessage());
        }
        return executeSaveAwbSelections(false);
    };

    $scope.saveHeader = function () {
        if ($scope.isCurrentBaggingLocked()) {
            setSaveFeedback('warning', $scope.getCurrentBaggingLockMessage());
            return toaster.pop('warning', 'Bagging', $scope.getCurrentBaggingLockMessage());
        }
        if (!$scope.headerForm.HostCode) {
            setSaveFeedback('warning', 'Host code wajib dipilih');
            return toaster.pop('warning', 'Bagging', 'Host code wajib dipilih');
        }
        if (!$scope.headerForm.DestinationHostCode) {
            setSaveFeedback('warning', 'Destination host code wajib dipilih');
            return toaster.pop('warning', 'Bagging', 'Destination host code wajib dipilih');
        }

        showConfirm({
            headerText: 'Simpan Header Bagging',
            bodyText: 'Simpan header bagging ini?'
        }, function () {
            if (!isClientOnline()) {
                return toaster.pop('warning', 'Bagging', 'Gagal menyimpan. Koneksi sedang offline.');
            }

            return maybeGenerateBaggingNumber(false, function (err) {
                if (err) {
                    return toaster.pop('error', 'Bagging', normalizeError(err));
                }

                return executeSaveHeader({}, function (saveErr) {
                    if (saveErr) {
                        setSaveFeedback('error', normalizeError(saveErr));
                        return toaster.pop('error', 'Bagging', normalizeError(saveErr));
                    }
                });
            });
        });
    };

    $scope.saveBulkItems = function () {
        if ($scope.isCurrentBaggingLocked()) {
            setSaveFeedback('warning', $scope.getCurrentBaggingLockMessage());
            return toaster.pop('warning', 'Bagging', $scope.getCurrentBaggingLockMessage());
        }
        showConfirm({
            headerText: 'Simpan Bulk AWB',
            bodyText: 'Simpan daftar AWB ke bagging ini?'
        }, function () {
            return executeSaveBulkItems();
        });
    };

    $scope.deleteItem = function (row) {
        showConfirm({
            headerText: 'Hapus Item AWB',
            bodyText: 'Hapus AWB ' + (row && row.awb ? row.awb : '-') + ' dari bagging ini?'
        }, function () {
            urlServices.hapusData(apiBase + '/' + encodeURIComponent($scope.headerForm.BagId) + '/items/' + encodeURIComponent(row.awb), {}, function (err) {
                if (err) {
                    return toaster.pop('error', 'Bagging', normalizeError(err));
                }

                loadBaggingDetail($scope.headerForm.BagId);
                toaster.pop('success', 'Bagging', 'AWB berhasil dihapus dari bagging');
            });
        });
    };

    $scope.savePrimaryAction = function () {
        if ($scope.isCurrentBaggingLocked()) {
            setSaveFeedback('warning', $scope.getCurrentBaggingLockMessage());
            return toaster.pop('warning', 'Bagging', $scope.getCurrentBaggingLockMessage());
        }
        if ($scope.awbDrafts.length) {
            return $scope.saveAwbSelections();
        }

        return $scope.saveHeader();
    };

    $scope.deleteCurrentBagging = function () {
        var bagId = normalizeTextValue($scope.headerForm.BagId);

        if (!$scope.canDeleteCurrentBagging()) {
            return true;
        }

        showConfirm({
            headerText: 'Hapus Bagging',
            bodyText: 'Hapus bagging ' + bagId + ' beserta seluruh detail AWB di dalamnya?'
        }, function () {
            return urlServices.hapusData(apiBase + '/' + encodeURIComponent(bagId), {}, function (err) {
                if (err) {
                    return toaster.pop('error', 'Bagging', normalizeError(err));
                }

                if ($window.history && $window.history.replaceState) {
                    $window.history.replaceState({}, $window.document ? $window.document.title : '', '/bagging');
                }
                $scope.resetHeader();
                return toaster.pop('success', 'Bagging', 'Bagging ' + bagId + ' berhasil dihapus');
            });
        });
    };

    $scope.$on('pilih-bagging-hub', function (event, args) {
        var hub = args && args.hub ? args.hub : null;

        if (!hub) {
            return true;
        }

        if (args.lookupType === 'destination') {
            $scope.headerForm.DestinationHostCode = hub.hub_id || '';
            $scope.onDestinationHostCodeChange();
            return true;
        }

        $scope.headerForm.HostCode = hub.hub_id || '';
        $scope.onHostCodeChange();
        return true;
    });

    $scope.$on('pilih-bagging-agent', function (event, args) {
        var agent = args && args.agent ? args.agent : null;

        if (!agent) {
            return true;
        }

        $scope.headerForm.AgentCode = agent.contact_reff || '';
        $scope.headerForm.AgentContactType = agent.contact_type || '';
        $scope.headerForm.AgentName = buildAgentDisplayValue(agent.contact_reff, agent.name || agent.agent_label);
        return true;
    });

    $scope.$on('pilih-bagging-entry', function (event, args) {
        var bagging = args && args.bagging ? args.bagging : null;

        if (!bagging || !bagging.bag_id) {
            return true;
        }

        $scope.searchBagId = bagging.bag_id;
        return loadBaggingDetail(bagging.bag_id, null, {
            updateUrl: true,
            clearOnError: true,
            preserveSearchValue: true
        });
    });

    $scope.resetHeader = function () {
        resetLoadedBaggingState();
        setSaveFeedback('', '');
    };

    $scope.searchBagging = function () {
        var typedBagId = normalizeUpperValue($scope.searchBagId);

        $scope.searchBagId = typedBagId;

        if (typedBagId) {
            return loadBaggingDetail(typedBagId, null, {
                clearOnError: true,
                updateUrl: true,
                preserveSearchValue: true
            });
        }

        return $scope.openBaggingLookup();
    };

    $scope.openBaggingLookupPopup = function () {
        return $scope.openBaggingLookup();
    };

    $scope.openBaggingDetail = function () {
        return $scope.searchBagging();
    };

    $scope.openPrintPreview = function () {
        var previewUrl = buildCurrentPrintPreviewUrl();

        if (!previewUrl) {
            return toaster.pop('warning', 'Bagging', 'Simpan nomor bagging terlebih dahulu sebelum cetak');
        }

        return $window.open(previewUrl, '_blank');
    };

    $scope.resetItem = function () {
        resetItemForm();
    };

    onlineEventHandler = function () {
        $scope.$evalAsync(function () {
            refreshOfflineState();
        });
    };

    offlineEventHandler = function () {
        $scope.$evalAsync(function () {
            refreshOfflineState();
        });
    };

    if ($window.addEventListener) {
        $window.addEventListener('online', onlineEventHandler);
        $window.addEventListener('offline', offlineEventHandler);
    }

    $scope.$on('$destroy', function () {
        stopCameraScan(true);
        if ($window.removeEventListener) {
            $window.removeEventListener('online', onlineEventHandler);
            $window.removeEventListener('offline', offlineEventHandler);
        }
    });

    $scope.suspendAutoNumberSync = true;
    syncManifestDateValue($scope.headerForm.ManifestDate);
    $scope.$evalAsync(function () {
        $scope.suspendAutoNumberSync = false;
    });
    refreshOfflineState();
    loadReferenceMasters(function () {
        var requestedBagId = getRequestedBagIdFromUrl();

        if (requestedBagId) {
            loadBaggingDetail(requestedBagId);
        }
    });
}]);